import { BlogCategory } from '../types/Blog';
import { API_BASE_URL } from './apiBase';

export const getBlogCategoriesFromServer = async (retryCount = 0): Promise<BlogCategory[]> => {
  try {
    console.log(`📚 Fetching blog categories from server... (attempt ${retryCount + 1})`);
    // Add cache-busting parameter to ensure fresh data
    const cacheBuster = Date.now();
    const response = await fetch(`${API_BASE_URL}/blog-categories?_cb=${cacheBuster}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const categories = await response.json();
    console.log('📚 Server returned blog categories:', categories.length);
    
    // Format categories to ensure they have all required fields
    const formattedCategories = categories.map((cat: any) => ({
      id: cat.id || cat._id?.toString(),
      name: cat.name,
      slug: cat.slug,
      description: cat.description || cat.intro || '',
      color: cat.color || '#8B4513',
      intro: cat.intro || cat.description || '',
      seoTitle: cat.seoTitle || '',
      metaDescription: cat.metaDescription || '',
      h1: cat.h1 || cat.name,
      createdAt: cat.createdAt,
      updatedAt: cat.updatedAt,
      order: cat.order || 0
    }));
    
    console.log('📚 Formatted server blog categories:', formattedCategories.length);
    return formattedCategories;
  } catch (error) {
    console.error(`Error fetching blog categories (attempt ${retryCount + 1}):`, error);
    
    // Retry logic
    if (retryCount < 2) {
      console.log('🔄 Retrying blog category fetch...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      return getBlogCategoriesFromServer(retryCount + 1);
    }
    
    throw error;
  }
};

export const saveBlogCategoriesToServer = async (categories: BlogCategory[]): Promise<boolean> => {
  try {
    console.log('📚 Saving blog categories to server:', categories.length);
    
    const response = await fetch(`${API_BASE_URL}/blog-categories`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(categories),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    console.log('📚 Blog categories saved to server:', result);
    return true;
  } catch (error) {
    console.error('Error saving blog categories to server:', error);
    return false;
  }
};

// Rename a blog category globally (updates blog posts too)
export const renameBlogCategoryOnServer = async (
  oldSlug: string,
  newSlug: string,
  newName?: string,
  newColor?: string
): Promise<{ success: boolean; updatedCategories?: number; updatedPosts?: number }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/blog-categories/rename`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ oldSlug, newSlug, newName, newColor }),
    });
    if (!response.ok) {
      return { success: false };
    }
    return await response.json();
  } catch (e) {
    console.error('Error renaming blog category on server:', e);
    return { success: false };
  }
};

export const loadLiveBlogCategories = async (): Promise<BlogCategory[]> => {
  try {
    console.log('🌐 LIVE MODE: Loading blog categories from server only...');
    const serverCategories = await getBlogCategoriesFromServer();
    console.log('✅ LIVE: Using server blog categories:', serverCategories.length);
    return serverCategories;
  } catch (error) {
    console.error('Error loading live blog categories:', error);
    return [];
  }
};
